/**
 * Hook: Facilities list state and query cache cleanup for Cleaning Standards page.
 * Single Responsibility: facilities list from search, stable change handler, cleanup on unmount.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FACILITIES_QUERY_KEY } from '../../../hooks/useFacilitiesInfinite';
import { FACILITIES_PAGE_BASED_QUERY_KEY } from '../../../hooks/useFacilitiesPageBased';
import { LOCATIONS_QUERY_KEY } from '../../../hooks/useLocationsInfinite';
import { QUERY_SCOPE } from '../constants';

/**
 * Pure: compute a stable key from facility list for dependency tracking.
 * @param {Array<{ id?: string }>} facilities
 * @returns {string}
 */
export function computeFacilityIdsKey(facilities) {
  return (facilities || [])
    .map((f) => f?.id)
    .filter(Boolean)
    .sort()
    .join(',');
}

export function useCleaningStandardsFacilities() {
  const queryClient = useQueryClient();
  const [facilitiesFromList, setFacilitiesFromList] = useState([]);
  const facilityIdsKeyRef = useRef('');

  const handleFacilitiesChange = useCallback((list) => {
    const next = list ?? [];
    const idsKey = computeFacilityIdsKey(next);
    if (idsKey === facilityIdsKeyRef.current) return;
    facilityIdsKeyRef.current = idsKey;
    setFacilitiesFromList(next);
  }, []);

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: [...FACILITIES_QUERY_KEY, QUERY_SCOPE] });
      queryClient.removeQueries({
        queryKey: [...FACILITIES_PAGE_BASED_QUERY_KEY, QUERY_SCOPE],
      });
      queryClient.removeQueries({ queryKey: [...LOCATIONS_QUERY_KEY, QUERY_SCOPE] });
    };
  }, [queryClient]);

  return {
    facilitiesFromList,
    handleFacilitiesChange,
    queryScope: QUERY_SCOPE,
  };
}
